import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Location } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, HostListener, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import {
  OffcanvasBodyComponent,
  OffcanvasComponent,
  OffcanvasHeaderComponent
} from '@coreui/angular-pro';
import { I18nService, TranslatePipe, TranslateService } from 'i18n';
import { ConsentComponent, SafeHtmlPipe } from 'shared';

type Policy = 'privacy' | 'terms' | 'imprint';

/**
 * Wraps every funnel page with:
 *   - card-stack background + dark overlay
 *   - top-right language switcher
 *   - bottom legal links (Privacy / TOS / Legal Notice) — open as offcanvas
 *   - "operated by Frontier Capital Holdings Ltd." strip (uses `login.footer` i18n)
 *
 * The offcanvas + policy-fetch flow mirrors libs/app/.../login-page so the UX
 * matches the rest of the app exactly.
 */
@Component({
  selector: 'app-funnel-shell',
  standalone: true,
  imports: [
    CommonModule,
    OffcanvasComponent,
    OffcanvasHeaderComponent,
    OffcanvasBodyComponent,
    ConsentComponent,
    SafeHtmlPipe,
    TranslatePipe
  ],
  templateUrl: './funnel-shell.component.html',
  styleUrls: ['./funnel-shell.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FunnelShellComponent {
  private i18n             = inject(I18nService);
  private translateService = inject(TranslateService);
  private router           = inject(Router);
  private location         = inject(Location);
  private http             = inject(HttpClient);
  private destroyRef       = inject(DestroyRef);

  /**
   * The languages this domain actually offers, not a hardcoded pair: config domain 4 publishes
   * ten (data/config/domain_config.json), and the story funnel at /r lets the visitor pick any
   * of them before landing here — a switcher that only offered EN/DE would strand them.
   */
  readonly languages = computed(() => {
    const codes = this.translateService.getAvailableLangs();
    return (codes?.length ? codes : ['en']).map(code => ({
      code,
      label: code.toUpperCase(),
      name: this.i18n.getLanguageName(code)
    }));
  });

  readonly langOpen = signal(false);

  /** `login.footer` already contains the "operated by Frontier Capital Holdings Ltd." HTML. */
  readonly footerText = toSignal(
    this.translateService.translateAsync('login.footer'),
    { initialValue: null }
  );

  // Offcanvas state
  policyVisible    = signal(false);
  policyLoading    = signal(false);
  policyContent    = signal<string>('');

  currentLang(): string {
    return this.i18n.getLanguage() || 'en';
  }

  toggleLangMenu(event: Event): void {
    event.stopPropagation();
    this.langOpen.update(open => !open);
  }

  @HostListener('document:click')
  closeLangMenu(): void {
    if (this.langOpen()) this.langOpen.set(false);
  }

  switchLanguage(code: string): void {
    this.langOpen.set(false);
    if (code === this.currentLang()) return;
    this.i18n.setLanguage(code, false);

    // Swap the language segment in place. The pattern allows a region suffix, or 'es-MX'
    // would not be recognised as the current language segment and the code would be prepended
    // to a path that already had one.
    const url = this.router.url;
    const swapped = url.replace(/^\/[a-z]{2}(-[A-Za-z]{2})?(\/|$)/, `/${code}$2`);
    this.location.replaceState(swapped);
  }

  openPolicy(policy: Policy): void {
    this.policyLoading.set(true);
    this.policyVisible.set(true);
    this.policyContent.set('');

    this.http.get(
      `/media/${this.currentLang()}/policy/${policy}`,
      { responseType: 'text' as 'text', params: { embed: 'true' } }
    )
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe({
      next: (content) => {
        this.policyContent.set(content || '');
        this.policyLoading.set(false);
      },
      error: () => {
        this.policyContent.set('<p>Could not load the policy. Please try again.</p>');
        this.policyLoading.set(false);
      }
    });
  }

  closePolicy(): void {
    this.policyVisible.set(false);
  }

  policyVisibleChange(visible: boolean): void {
    this.policyVisible.set(visible);
  }
}
